/**
 * Connection Manager
 * Handles WebSocket connection lifecycle with automatic reconnection
 */

import { EventEmitter } from './EventEmitter.js';
import { ConnectionError } from '../errors/VoiceMateErrors.js';

export class ConnectionManager extends EventEmitter {
  constructor(config, logger) {
    super();
    this.config = config;
    this.logger = logger;
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.lastWsUrl = null;

    // Message queue for offline buffering
    this.messageQueue = [];

    // Stats tracking
    this.stats = {
      messagesSent: 0,
      messagesReceived: 0,
      reconnects: 0,
      errors: 0
    };
  }

  /**
   * Connect to WebSocket
   * @param {string} wsUrl - WebSocket URL
   * @returns {Promise<void>}
   * @throws {ConnectionError} If connection fails
   */
  async connect(wsUrl) {
    return new Promise((resolve, reject) => {
      this.lastWsUrl = wsUrl;
      this.logger.info(`Connecting to ${wsUrl}`);
      this.emit('connecting');

      try {
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = 'arraybuffer';

        const connectTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            this.ws.close();
            reject(new ConnectionError('Connection timeout', {
              url: wsUrl,
              timeout: 30000
            }));
          }
        }, 30000);

        this.ws.onopen = () => {
          clearTimeout(connectTimeout);
          this.logger.info('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;

          // Flush queued messages
          this.flushMessageQueue();

          this.emit('connected');
          resolve();
        };

        this.ws.onmessage = (event) => this.handleMessage(event);

        this.ws.onerror = (error) => {
          clearTimeout(connectTimeout);
          this.logger.error('WebSocket error:', error);
          this.stats.errors++;
          this.emit('error', new ConnectionError('WebSocket error', {
            url: wsUrl,
            error: error
          }));
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectTimeout);
          this.logger.info(`WebSocket closed: ${event.code} - ${event.reason}`);
          this.isConnected = false;
          this.emit('disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });

          // Auto-reconnect if enabled and not a clean close
          if (this.config.autoReconnect && event.code !== 1000 && this.reconnectAttempts < this.config.maxReconnectAttempts) {
            this.attemptReconnect();
          } else if (event.code !== 1000) {
            reject(new ConnectionError('Connection closed unexpectedly', {
              code: event.code,
              reason: event.reason
            }));
          }
        };

      } catch (error) {
        this.logger.error('WebSocket creation failed:', error);
        reject(new ConnectionError('Failed to create WebSocket', {
          error: error.message,
          url: wsUrl
        }));
      }
    });
  }

  /**
   * Handle incoming WebSocket message
   * @param {MessageEvent} event - Message event
   */
  handleMessage(event) {
    this.stats.messagesReceived++;

    if (event.data instanceof ArrayBuffer) {
      // Binary audio frame
      this.emit('audio:data', event.data);
    } else {
      // JSON control message
      try {
        const message = JSON.parse(event.data);
        this.emit('message', message);
      } catch (error) {
        this.logger.warn('Failed to parse message:', error);
      }
    }
  }

  /**
   * Send JSON message
   * @param {Object|string} message - Message to send
   * @returns {boolean} Success status
   */
  send(message) {
    if (!this.isConnected) {
      this.messageQueue.push(message);
      return false;
    }

    try {
      const data = typeof message === 'string' ? message : JSON.stringify(message);
      this.ws.send(data);
      this.stats.messagesSent++;
      return true;
    } catch (error) {
      this.logger.error('Send failed:', error);
      this.stats.errors++;
      return false;
    }
  }

  /**
   * Send binary data
   * @param {ArrayBuffer} arrayBuffer - Binary data to send
   * @returns {boolean} Success status
   */
  sendBinary(arrayBuffer) {
    if (!this.isConnected) {
      return false;
    }

    try {
      this.ws.send(arrayBuffer);
      this.stats.messagesSent++;
      return true;
    } catch (error) {
      this.logger.error('Binary send failed:', error);
      this.stats.errors++;
      return false;
    }
  }

  /**
   * Attempt to reconnect
   */
  async attemptReconnect() {
    this.reconnectAttempts++;
    this.stats.reconnects++;

    if (this.reconnectAttempts > this.config.maxReconnectAttempts) {
      this.logger.error('Max reconnect attempts reached');
      this.emit('reconnect:failed');
      return;
    }

    const delay = this.config.reconnectDelay * this.reconnectAttempts; // Exponential backoff

    this.logger.info(
      `Reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`
    );
    this.emit('reconnecting', this.reconnectAttempts);

    await this.delay(delay);

    try {
      await this.connect(this.lastWsUrl);
      this.logger.info('Reconnected successfully');
      this.emit('reconnected');
    } catch (error) {
      this.logger.warn('Reconnect failed, will retry');
      // attemptReconnect will be called again by onclose handler
    }
  }

  /**
   * Flush queued messages
   */
  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  /**
   * Disconnect WebSocket
   * @param {number} code - Close code (default: 1000 = normal closure)
   * @param {string} reason - Close reason
   */
  disconnect(code = 1000, reason = 'Client disconnect') {
    if (this.ws) {
      // Prevent auto-reconnect on intentional disconnect
      this.reconnectAttempts = this.config.maxReconnectAttempts + 1;

      this.ws.close(code, reason);
      this.ws = null;
      this.isConnected = false;
    }
  }

  /**
   * Get connection statistics
   * @returns {Object} Statistics
   */
  getStats() {
    return {
      ...this.stats,
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: this.messageQueue.length
    };
  }

  /**
   * Delay helper
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
